/**
 * PostTradeAnalysis.jsx
 *
 * Fully self-contained AI trade analysis — NO API KEY, NO BACKEND.
 * Built-in rule-based intelligence engine analyzes every trade locally.
 *
 * INTEGRATION (TVChartContainer.jsx)
 * ────────────────────────────────────
 * 1. import PostTradeAnalysis from './PostTradeAnalysis';
 * 2. const [closedTradeData, setClosedTradeData] = useState(null);
 * 3. After TP/SL/Manual close → setClosedTradeData({ ...tradeSnapshot })
 * 4. <PostTradeAnalysis tradeData={closedTradeData} onClose={() => setClosedTradeData(null)} isDarkMode={isDarkMode} />
 *
 * tradeData shape:
 * {
 *   symbol, side (1|-1), entry, exit, tp, sl,
 *   lots, leverage, pnl, closeReason ("TP"|"SL"|"MANUAL_CLOSE"),
 *   marginUsed, currency ("USD"|"INR")
 * }
 */

import React, { useState, useEffect, useRef } from "react";

// ══════════════════════════════════════════════════════════════════════════════
// 🧠  BUILT-IN AI ENGINE  —  zero external dependencies
// ══════════════════════════════════════════════════════════════════════════════

const TradeAI = {

    // ── helpers ──────────────────────────────────────────────────────────────

    getRR(tp, sl, entry) {
        if (!tp || !sl || !entry) return null;
        const reward = Math.abs(tp - entry);
        const risk = Math.abs(sl - entry);
        return risk > 0 ? reward / risk : null;
    },

    getPipMove(symbol, entry, exit, side) {
        const raw = (exit - entry) * side;
        if (symbol.includes("JPY")) return raw * 100;
        if (["XAUUSD", "XAGUSD", "BTCUSD", "ETHUSD"].some(s => symbol.includes(s))) return raw;
        return raw * 10000;
    },

    getSLTightness(symbol, slDistance) {
        // Average "noise" distance by asset class
        const noiseMap = {
            XAUUSD: 1.5, XAGUSD: 0.15, BTCUSD: 200, ETHUSD: 20,
            USDJPY: 0.15, EURJPY: 0.2, GBPJPY: 0.25,
            EURUSD: 0.0015, GBPUSD: 0.0018, AUDUSD: 0.0013,
        };
        let noise = 0.0015; // default forex
        for (const [key, val] of Object.entries(noiseMap)) {
            if (symbol.toUpperCase().includes(key)) { noise = val; break; }
        }
        if (slDistance < noise * 0.6) return "too_tight";
        if (slDistance > noise * 5) return "too_wide";
        return "ok";
    },

    getLeverageRisk(leverage) {
        if (leverage > 200) return "extreme";
        if (leverage > 100) return "very_high";
        if (leverage > 50) return "high";
        if (leverage > 20) return "moderate";
        return "conservative";
    },

    // ── main analysis engine ──────────────────────────────────────────────────

    analyze(trade) {
        const {
            symbol = "UNKNOWN",
            side = 1,
            entry = 0,
            exit = 0,
            tp = null,
            sl = null,
            lots = 1,
            leverage = 1,
            pnl = 0,
            closeReason = "MANUAL_CLOSE",
            marginUsed = 0,
            currency = "USD",
        } = trade;

        const sym = currency === "INR" ? "₹" : "$";
        const isProfit = pnl >= 0;
        const isBuy = side === 1;
        const direction = isBuy ? "BUY" : "SELL";
        const rr = this.getRR(tp, sl, entry);
        const rrStr = rr ? rr.toFixed(2) : "N/A";
        const pipMove = this.getPipMove(symbol, entry, exit, side);
        const slDist = sl ? Math.abs(entry - sl) : null;
        const tpDist = tp ? Math.abs(tp - entry) : null;
        const slTight = slDist ? this.getSLTightness(symbol, slDist) : "no_sl";
        const levRisk = this.getLeverageRisk(leverage);
        const absPnl = Math.abs(pnl).toFixed(2);

        // ── score calculation ─────────────────────────────────────────────────
        let score = 5; // neutral baseline

        // R:R scoring
        if (rr !== null) {
            if (rr >= 3) score += 2;
            else if (rr >= 2) score += 1.5;
            else if (rr >= 1) score += 0.5;
            else score -= 2;
        } else {
            score -= 1; // no TP/SL set
        }

        // SL quality
        if (slTight === "too_tight") score -= 1.5;
        else if (slTight === "too_wide") score -= 0.5;
        else if (slTight === "ok") score += 1;
        else if (slTight === "no_sl") score -= 2;

        // Leverage
        if (levRisk === "extreme") score -= 2;
        else if (levRisk === "very_high") score -= 1;
        else if (levRisk === "conservative") score += 0.5;

        // Outcome
        if (closeReason === "TP") score += 1;
        if (closeReason === "SL" && rr !== null && rr < 1) score -= 1;

        // TP not set
        if (!tp) score -= 1;
        if (!sl) score -= 2;

        score = Math.max(1, Math.min(10, Math.round(score)));

        const scoreLabel =
            score >= 9 ? "Excellent" :
                score >= 7 ? "Good" :
                    score >= 5 ? "Average" :
                        score >= 3 ? "Poor" : "Terrible";

        // ── verdict ────────────────────────────────────────────────────────────
        const verdictPool = {
            TP: {
                high_rr: `**Disciplined win** — solid R:R of ${rrStr} paid off exactly as planned.`,
                low_rr: `**Lucky win** — TP hit but R:R of ${rrStr} makes this barely worth the risk.`,
                no_sl: `**Reckless profit** — TP hit with no stop loss; one bad trade could wipe this gain.`,
                high_lev: `**High-leverage gamble** — ${leverage}x worked this time, but risk was disproportionate.`,
                default: `**Clean execution** — trade closed at target with ${rrStr} R:R.`,
            },
            SL: {
                too_tight: `**Self-inflicted loss** — stop loss at ${slDist?.toFixed(5)} was too tight for normal market noise.`,
                no_tp: `**Directionless trade** — no take profit defined; only planned for loss, not gain.`,
                low_rr: `**Poor risk management** — risked ${sym}${absPnl} for a reward smaller than the risk.`,
                high_lev: `**Leverage destroyed margin** — ${leverage}x amplified a small move into a ${sym}${absPnl} loss.`,
                default: `**Stopped out** — price moved ${pipMove.toFixed(1)} pips against the ${direction} position.`,
            },
            MANUAL_CLOSE: {
                profit: `**Early exit** — position manually closed at ${sym}${absPnl} profit before TP could be reached.`,
                loss: `**Cut the loss manually** — exited at ${sym}${absPnl} loss before SL was hit.`,
                default: `**Manual intervention** — trader overrode the trade plan and closed early.`,
            },
        };

        let verdict;
        if (closeReason === "TP") {
            if (rr && rr >= 2) verdict = verdictPool.TP.high_rr;
            else if (rr && rr < 1) verdict = verdictPool.TP.low_rr;
            else if (!sl) verdict = verdictPool.TP.no_sl;
            else if (leverage > 100) verdict = verdictPool.TP.high_lev;
            else verdict = verdictPool.TP.default;
        } else if (closeReason === "SL") {
            if (slTight === "too_tight") verdict = verdictPool.SL.too_tight;
            else if (!tp) verdict = verdictPool.SL.no_tp;
            else if (rr && rr < 1) verdict = verdictPool.SL.low_rr;
            else if (leverage > 100) verdict = verdictPool.SL.high_lev;
            else verdict = verdictPool.SL.default;
        } else {
            if (isProfit) verdict = verdictPool.MANUAL_CLOSE.profit;
            else verdict = verdictPool.MANUAL_CLOSE.loss;
        }

        // ── what happened ──────────────────────────────────────────────────────
        const moveDesc = pipMove >= 0
            ? `moved ${Math.abs(pipMove).toFixed(1)} pips in your favour`
            : `moved ${Math.abs(pipMove).toFixed(1)} pips against you`;

        let whatHappened;
        if (closeReason === "TP") {
            whatHappened = `A ${direction} trade on **${symbol}** was entered at **${entry}** with a take profit at **${tp?.toFixed(5) ?? "—"}**. Price ${moveDesc} and hit the TP target at **${exit}**, closing the trade for a **profit of ${sym}${absPnl}**. The trade performed as planned with no manual intervention required.`;
        } else if (closeReason === "SL") {
            whatHappened = `A ${direction} trade on **${symbol}** was opened at **${entry}** with a stop loss at **${sl?.toFixed(5) ?? "—"}**. Price ${moveDesc} and triggered the stop loss at **${exit}**, resulting in a **loss of ${sym}${absPnl}**. The trade was automatically closed by the risk management system.`;
        } else {
            const outcome = isProfit ? `a profit of ${sym}${absPnl}` : `a loss of ${sym}${absPnl}`;
            whatHappened = `A ${direction} trade on **${symbol}** was entered at **${entry}**. Price ${moveDesc}. The trader **manually closed** the position at **${exit}** for ${outcome}, overriding the original trade plan before TP or SL was reached.`;
        }

        // ── main reason ────────────────────────────────────────────────────────
        let mainReason;
        if (closeReason === "TP") {
            if (rr && rr >= 2) {
                mainReason = `**Strong R:R of 1:${rrStr}** was the primary driver. Risking ${sym}${(Math.abs(pnl) / rr).toFixed(2)} to make ${sym}${absPnl} is mathematically sound — even a 33% win rate would be profitable at this ratio.`;
            } else if (rr && rr < 1) {
                mainReason = `Despite hitting TP, the **R:R of 1:${rrStr} is below 1:1** — this means you need to win more than 50% of trades just to break even. The trade won but the structure was not sound.`;
            } else {
                mainReason = `Trade closed at take profit with **${pipMove.toFixed(1)} pips** of movement in your favour. Price action respected your target level, validating the directional bias.`;
            }
        } else if (closeReason === "SL") {
            if (slTight === "too_tight") {
                mainReason = `**Stop loss was too tight at ${slDist?.toFixed(5)}** — this is likely inside the normal noise range for ${symbol}. Price barely moved before stopping you out. A wider, structure-based SL would have survived this move.`;
            } else if (!tp) {
                mainReason = `**No take profit was defined.** Trading without a TP means the trade had no reward target — you accepted all the downside risk with no defined upside. This is the primary structural flaw.`;
            } else if (rr && rr < 1) {
                mainReason = `**R:R of 1:${rrStr} is the core problem** — you risked ${sym}${absPnl} to potentially make ${sym}${(pnl * rr).toFixed(2)}. A trade where the risk exceeds the reward requires a win rate above 50% just to break even.`;
            } else {
                mainReason = `Price moved ${Math.abs(pipMove).toFixed(1)} pips against the ${direction} and hit the stop loss at **${exit}**. The market invalidated the trade thesis by breaking the key level defined by the SL.`;
            }
        } else {
            if (isProfit) {
                mainReason = `**Early exit captured ${sym}${absPnl}** but left potential profit on the table if TP was set at **${tp?.toFixed(5) ?? "a higher target"}**. Manual closes before TP are often driven by fear of giving back gains rather than by price action signals.`;
            } else {
                mainReason = `**Manual close at a loss of ${sym}${absPnl}** before the stop loss was hit. This may reflect **emotional decision-making** — exiting prematurely on drawdown rather than trusting the original trade plan and stop loss level.`;
            }
        }

        // ── entry analysis ─────────────────────────────────────────────────────
        let entryAnalysis;
        const entryMoveRatio = slDist ? Math.abs(exit - entry) / slDist : null;

        if (closeReason === "TP") {
            entryAnalysis = `Entry at **${entry}** proved correct — price moved in the intended ${direction} direction and reached the target. `;
            if (rr && rr >= 2) entryAnalysis += `The entry likely had good confluence with a clear structure level, allowing a tight risk for a large reward.`;
            else entryAnalysis += `Consider whether the entry had sufficient confluence (trend, S/R, indicator alignment) to justify the trade.`;
        } else if (closeReason === "SL") {
            entryAnalysis = `Entry at **${entry}** was invalidated — price moved opposite to the ${direction} direction. `;
            if (slTight === "too_tight") {
                entryAnalysis += `The entry may have been in a **choppy zone** with no clear direction, or the stop was placed inside the spread/noise rather than below a key structure level.`;
            } else {
                entryAnalysis += `Review whether there was **sufficient confluence** at entry: trend alignment, key S/R level, indicator confirmation, and correct session timing.`;
            }
        } else {
            entryAnalysis = `Entry at **${entry}** led to a manually closed trade. `;
            entryAnalysis += isProfit
                ? `The directional bias was correct. Consider setting a TP next time and letting the market reach it rather than manually exiting.`
                : `Price initially moved against the entry. Review if the entry was counter-trend or lacked confirmation before entry.`;
        }

        // ── SL analysis ────────────────────────────────────────────────────────
        let slAnalysis;
        if (!sl) {
            slAnalysis = `**No stop loss was set** — this is a critical risk management failure. Without a SL, a single adverse move can wipe a significant portion of the account. Always define the maximum acceptable loss before entering.`;
        } else if (slTight === "too_tight") {
            slAnalysis = `SL at **${sl?.toFixed(5)}** is **${slDist?.toFixed(5)} away from entry** — this is likely too tight for ${symbol}'s normal volatility. Market noise alone can trigger this level. Place SL beyond the nearest significant structure: swing high/low, support/resistance, or ATR-based distance.`;
        } else if (slTight === "too_wide") {
            slAnalysis = `SL at **${sl?.toFixed(5)}** is **${slDist?.toFixed(5)} from entry** — quite wide. While this avoids premature stops, it increases the required margin and reduces position sizing efficiency. Ensure the wide SL is justified by a clear structural level.`;
        } else {
            slAnalysis = `SL at **${sl?.toFixed(5)}** appears to be at a **reasonable distance** of ${slDist?.toFixed(5)} from entry. Ensure it was placed at a structural level (below swing low for buys, above swing high for sells) rather than a fixed pip distance.`;
        }

        // ── R:R analysis ───────────────────────────────────────────────────────
        let rrAnalysis;
        if (!rr) {
            rrAnalysis = `**R:R cannot be calculated** — either TP or SL was not set. A defined R:R is fundamental to professional trading. Without both levels, it is impossible to evaluate whether the potential reward justified the risk.`;
        } else if (rr >= 3) {
            rrAnalysis = `**Excellent R:R of 1:${rrStr}** — you only need to win 25% of trades to be profitable at this ratio. This is the hallmark of professional risk management: small risk, large reward.`;
        } else if (rr >= 2) {
            rrAnalysis = `**Good R:R of 1:${rrStr}** — requires a 33% win rate to break even. This is a sound structure and represents disciplined planning. Maintain this standard consistently.`;
        } else if (rr >= 1) {
            rrAnalysis = `**Acceptable but suboptimal R:R of 1:${rrStr}** — requires a 50% win rate to break even. Consider widening TP or tightening SL to achieve at least 1:2 on future trades for better statistical edge.`;
        } else {
            rrAnalysis = `**Poor R:R of 1:${rrStr}** — you risked MORE than you stood to gain. This requires a win rate above ${Math.round(100 / (1 + rr))}% just to break even. Even experienced traders cannot sustain this long-term. Never take a trade with R:R below 1:1.`;
        }

        // ── mistakes ───────────────────────────────────────────────────────────
        const mistakes = [];

        if (!sl) mistakes.push("**No stop loss set** — trading without a SL is gambling, not trading. One unprotected loss can erase multiple wins.");
        else if (slTight === "too_tight") mistakes.push(`**Stop loss too tight** (${slDist?.toFixed(5)}) — placed inside market noise, causing premature exit before the idea had time to play out.`);
        else if (slTight === "too_wide") mistakes.push(`**Stop loss too wide** (${slDist?.toFixed(5)}) — unnecessarily large risk exposure. A tighter, structure-based SL improves R:R and reduces losses.`);

        if (!tp) mistakes.push("**No take profit set** — no reward target means no defined exit plan. Price could reverse at any point and the trade has no automatic lock-in of gains.");
        else if (rr && rr < 1) mistakes.push(`**R:R below 1:1** (1:${rrStr}) — risking more than the potential reward is statistically losing even with a 50% win rate.`);

        if (leverage > 200) mistakes.push(`**Extreme leverage of ${leverage}x** — a 0.5% adverse move wipes the entire margin. This level of leverage is unsuitable for any strategy.`);
        else if (leverage > 100) mistakes.push(`**Very high leverage of ${leverage}x** — small price fluctuations create disproportionate losses. Consider reducing to 10–50x for more controlled exposure.`);

        if (closeReason === "MANUAL_CLOSE" && !isProfit) mistakes.push("**Emotional exit before SL** — closing manually at a loss without a price action reason suggests panic. Trust your original stop loss placement.");
        if (closeReason === "MANUAL_CLOSE" && isProfit && tp) mistakes.push("**Left money on the table** — manually closed before TP was hit. This breaks trading discipline and reduces average win size over time.");
        if (closeReason === "SL" && rr && rr >= 2) mistakes.push("**Good setup, poor timing** — R:R was solid but entry was likely mistimed. Wait for additional confirmation (candle close, volume, session open) before entering.");

        if (mistakes.length === 0) {
            mistakes.push("Trade structure was sound — maintain this discipline on every trade.");
            mistakes.push("Continue verifying R:R, SL placement, and confluence before every entry.");
        }

        while (mistakes.length < 2) mistakes.push("Review confluence factors (trend, S/R, indicator) before the next entry.");

        // ── what worked ────────────────────────────────────────────────────────
        const whatWorked = [];

        if (sl) whatWorked.push(`**Stop loss was defined** at ${sl?.toFixed(5)} — risk was quantified before entry, which is the foundation of professional trading.`);
        if (tp) whatWorked.push(`**Take profit was set** at ${tp?.toFixed(5)} — reward target was defined, enabling a measurable R:R ratio.`);
        if (rr && rr >= 2) whatWorked.push(`**Strong R:R of 1:${rrStr}** — the trade was structured to make significantly more than it risked.`);
        if (closeReason === "TP") whatWorked.push(`**Trade reached take profit** — price action validated the directional bias and the exit was clean.`);
        if (levRisk === "conservative") whatWorked.push(`**Conservative leverage of ${leverage}x** — position sizing was prudent, protecting the account from excessive drawdown.`);
        if (closeReason === "SL" && rr && rr >= 2) whatWorked.push(`**Good risk:reward setup** — even though this trade lost, the structure was correct. A 1:${rrStr} R:R means you can be right less than 34% of the time and still be profitable.`);
        if (closeReason === "MANUAL_CLOSE" && isProfit) whatWorked.push(`**Captured a profit** — the directional bias was correct and the trade was exited in the green.`);

        if (whatWorked.length === 0) whatWorked.push("Trade was taken in a defined direction with a position size — the basics of trade structure were present.");
        while (whatWorked.length < 2) whatWorked.push("Keep refining entry criteria and SL/TP placement based on analysis of each trade.");

        // ── psychology note ────────────────────────────────────────────────────
        let psychologyNote;
        if (closeReason === "MANUAL_CLOSE" && !isProfit) {
            psychologyNote = `**Loss aversion** is likely at play — closing before the SL suggests fear of further loss overrode the original plan; trust your pre-defined levels.`;
        } else if (closeReason === "MANUAL_CLOSE" && isProfit) {
            psychologyNote = `**Fear of giving back profits** caused early exit — this "lock-in gains" impulse cuts winning trades short and is one of the most common profitability killers.`;
        } else if (closeReason === "SL" && leverage > 100) {
            psychologyNote = `**Overconfidence with leverage** — using ${leverage}x suggests a desire for outsized gains, which often reflects emotional decision-making rather than disciplined strategy.`;
        } else if (closeReason === "SL" && slTight === "too_tight") {
            psychologyNote = `**Impatience at entry** — a very tight SL often indicates entering before price reached the ideal level, accepting a worse entry to avoid "missing" the move.`;
        } else if (closeReason === "TP" && rr && rr >= 2) {
            psychologyNote = `**Disciplined patience** — letting the trade run to the TP target without interfering shows strong psychological control and trust in the system.`;
        } else if (closeReason === "TP" && rr && rr < 1) {
            psychologyNote = `**Risk-seeking bias** — setting a TP closer than the SL suggests a preference for frequent small wins, which is psychologically comfortable but statistically unsound.`;
        } else {
            psychologyNote = `Review your emotional state at the time of entry — ensure trades are taken based on **confluent setups**, not boredom, FOMO, or revenge trading.`;
        }

        // ── actionable tip ─────────────────────────────────────────────────────
        let actionableTip;
        if (!sl) {
            actionableTip = `Before your next entry, define your SL level FIRST by identifying the nearest structural invalidation point, then calculate position size so the SL loss equals a fixed % (e.g. 1–2%) of your account balance.`;
        } else if (rr && rr < 1) {
            actionableTip = `On your next trade, only enter if TP is at least 2× the distance of your SL from entry — if the setup doesn't offer 1:2 R:R, skip it and wait for the next one.`;
        } else if (slTight === "too_tight") {
            actionableTip = `Place your next SL below the most recent swing low (for buys) or above the most recent swing high (for sells) on the chart — use structure, not a fixed pip count.`;
        } else if (leverage > 100) {
            actionableTip = `Reduce leverage to maximum 50x on the next trade and calculate position size so a full SL loss equals no more than 2% of account balance.`;
        } else if (closeReason === "MANUAL_CLOSE" && !isProfit) {
            actionableTip = `On the next trade, write your SL level and the reason for it before entering — then commit to only closing manually if that specific reason is invalidated by price action.`;
        } else if (closeReason === "MANUAL_CLOSE" && isProfit && tp) {
            actionableTip = `Set a rule: once a trade is open, only close it manually if price gives a clear reversal signal (e.g. opposite candle pattern at resistance) — otherwise let TP do its job.`;
        } else if (rr && rr >= 2 && closeReason === "TP") {
            actionableTip = `Your structure is working — focus on increasing trade frequency on similar setups and tracking win rate over the next 20 trades to measure true edge.`;
        } else {
            actionableTip = `Before your next entry, verify 3 confluent factors: (1) trend direction on higher TF, (2) entry at key S/R level, (3) R:R of at least 1:2 — only enter when all 3 align.`;
        }

        // ── pre-trade checklist ────────────────────────────────────────────────
        const nextTradeChecklist = [
            `**Define SL first** — identify structural invalidation level before entry price`,
            `**Confirm R:R ≥ 1:2** — only enter if TP distance is at least 2× the SL distance`,
            `**Check higher timeframe trend** — ensure the trade direction aligns with H4/Daily bias`,
        ];

        if (!sl || slTight === "too_tight") {
            nextTradeChecklist.push(`**SL at structure** — place below swing low (buys) or above swing high (sells), not a fixed pip count`);
        }
        if (leverage > 100) {
            nextTradeChecklist.push(`**Reduce leverage** — max 50x; size position so full SL loss = 1-2% of balance`);
        }
        if (closeReason === "MANUAL_CLOSE") {
            nextTradeChecklist.push(`**No manual close unless price invalidates thesis** — trust the SL and TP levels set at entry`);
        }

        return {
            verdict,
            score,
            scoreLabel,
            whatHappened,
            mainReason,
            mistakes: mistakes.slice(0, 3),
            whatWorked: whatWorked.slice(0, 3),
            entryAnalysis,
            slAnalysis,
            rrAnalysis,
            psychologyNote,
            actionableTip,
            nextTradeChecklist: nextTradeChecklist.slice(0, 4),
        };
    }
};

// ══════════════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

const Md = ({ text }) => {
    if (!text) return null;
    return (
        <>
            {text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
                p.startsWith("**") && p.endsWith("**")
                    ? <strong key={i}>{p.slice(2, -2)}</strong>
                    : <span key={i}>{p}</span>
            )}
        </>
    );
};

const Stat = ({ label, value, color, dark }) => (
    <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "7px 0", borderBottom: `1px solid ${dark ? "#21262d" : "#e8eaef"}`,
    }}>
        <span style={{ fontSize: 11, color: dark ? "#7d8590" : "#5d606b" }}>{label}</span>
        <span style={{
            fontSize: 12, fontWeight: 600,
            color: color || (dark ? "#e6edf3" : "#131722"),
            fontFamily: "'SF Mono','Fira Code',monospace",
        }}>{value}</span>
    </div>
);

const ScoreRing = ({ score, label, color }) => {
    const r = 30, circ = 2 * Math.PI * r;
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
            <svg width={76} height={76} viewBox="0 0 76 76">
                <circle cx={38} cy={38} r={r} fill="none" stroke="#2a2e39" strokeWidth={6} />
                <circle cx={38} cy={38} r={r} fill="none" stroke={color} strokeWidth={6}
                    strokeDasharray={`${(score / 10) * circ} ${circ}`}
                    strokeLinecap="round" transform="rotate(-90 38 38)"
                    style={{ transition: "stroke-dasharray 1.2s cubic-bezier(.16,1,.3,1)" }}
                />
                <text x={38} y={40} textAnchor="middle" dominantBaseline="middle"
                    fill={color} fontSize={20} fontWeight={800}
                    fontFamily="'SF Mono','Fira Code',monospace">{score}</text>
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {label}
            </span>
        </div>
    );
};

const Card = ({ icon, title, accent, dark, delay = 0, children }) => (
    <div className="pta-card" style={{
        borderRadius: 11, border: `1px solid ${accent}30`,
        background: dark
            ? `linear-gradient(135deg,${accent}09 0%,transparent 100%)`
            : `linear-gradient(135deg,${accent}07 0%,#fff 100%)`,
        padding: "13px 15px", marginBottom: 10,
        animationDelay: `${delay}s`,
    }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
            <span style={{ fontSize: 15 }}>{icon}</span>
            <span style={{
                fontWeight: 700, fontSize: 11, color: accent,
                textTransform: "uppercase", letterSpacing: "0.07em",
            }}>{title}</span>
        </div>
        {children}
    </div>
);

const BulletList = ({ items, color, dark }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(items || []).map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                <span style={{
                    width: 19, height: 19, borderRadius: 5, flexShrink: 0,
                    background: `${color}20`, border: `1px solid ${color}40`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, color, fontWeight: 700, marginTop: 1,
                }}>{i + 1}</span>
                <span style={{ fontSize: 12.5, color: dark ? "#c4ccd8" : "#2a2e39", lineHeight: 1.6 }}>
                    <Md text={item} />
                </span>
            </div>
        ))}
    </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

const PostTradeAnalysis = ({ tradeData, onClose, isDarkMode = true }) => {
    const [analysis, setAnalysis] = useState(null);
    const prevTradeRef = useRef(null);

    const dark = isDarkMode;
    const c = {
        bg: dark ? "#0d1117" : "#ffffff",
        panel: dark ? "#131722" : "#f8f9fb",
        border: dark ? "#21262d" : "#e0e3eb",
        text: dark ? "#e6edf3" : "#131722",
        muted: dark ? "#7d8590" : "#5d606b",
    };

    // Run analysis synchronously when new trade data arrives
    useEffect(() => {
        if (!tradeData) { setAnalysis(null); return; }
        // avoid re-running for same trade
        const key = `${tradeData.entry}_${tradeData.exit}_${tradeData.closeReason}`;
        if (prevTradeRef.current === key) return;
        prevTradeRef.current = key;
        setAnalysis(TradeAI.analyze(tradeData));
    }, [tradeData]);

    if (!tradeData) return null;

    const {
        symbol = "UNKNOWN", side = 1, entry = 0, exit = 0,
        tp = null, sl = null, lots = 1, leverage = 1,
        pnl = 0, closeReason = "MANUAL_CLOSE",
        currency = "USD", marginUsed = 0,
    } = tradeData;

    const sym = currency === "INR" ? "₹" : "$";
    const isProfit = pnl >= 0;
    const rr = tp && sl ? Math.abs((tp - entry) / (sl - entry)).toFixed(2) : "N/A";
    const outcomeColor = closeReason === "TP" ? "#089981" : closeReason === "SL" ? "#f23645" : "#ffc107";
    const outcomeLabel = closeReason === "TP" ? "🎯 Take Profit" : closeReason === "SL" ? "🛑 Stop Loss" : "✋ Manual Close";
    const scoreColor = !analysis ? "#2962ff"
        : analysis.score >= 8 ? "#089981"
            : analysis.score >= 5 ? "#ffc107"
                : "#f23645";

    return (
        <>
            <style>{`
                @keyframes overlayIn { from{opacity:0;}to{opacity:1;} }
                @keyframes panelIn   { from{transform:translate(-50%,-48%) scale(.97);opacity:0;}to{transform:translate(-50%,-50%) scale(1);opacity:1;} }
                @keyframes cardIn    { from{transform:translateY(10px);opacity:0;}to{transform:translateY(0);opacity:1;} }
                .pta-card  { animation:cardIn .32s ease forwards; opacity:0; }
                .pta-close:hover { background:rgba(242,54,69,.15) !important; color:#f23645 !important; }
                .pta-cont:hover  { background:#1a56d6 !important; }
                ::-webkit-scrollbar{width:4px;}
                ::-webkit-scrollbar-track{background:transparent;}
                ::-webkit-scrollbar-thumb{background:#434651;border-radius:4px;}
            `}</style>

            {/* Backdrop */}
            <div onClick={onClose} style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,.82)",
                backdropFilter: "blur(8px)", zIndex: 999998, animation: "overlayIn .2s ease",
            }} />

            {/* Panel */}
            <div style={{
                position: "fixed", top: "50%", left: "50%",
                transform: "translate(-50%,-50%)",
                width: "min(880px,96vw)", height: "min(740px,94vh)",
                background: c.bg, border: `1px solid ${c.border}`,
                borderRadius: 18, zIndex: 999999,
                display: "flex", flexDirection: "column",
                overflow: "hidden", boxShadow: "0 40px 100px rgba(0,0,0,.85)",
                animation: "panelIn .3s cubic-bezier(.16,1,.3,1)",
            }} onClick={e => e.stopPropagation()}>

                {/* ── Header ── */}
                <div style={{
                    padding: "14px 20px", borderBottom: `1px solid ${c.border}`,
                    background: c.panel, display: "flex", alignItems: "center",
                    justifyContent: "space-between", flexShrink: 0,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                            width: 38, height: 38, borderRadius: 10,
                            background: "linear-gradient(135deg,#2962ff,#1a3fcf)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 18, boxShadow: "0 4px 14px rgba(41,98,255,.45)",
                        }}>🤖</div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: c.text }}>
                                AI Lubdha
                            </div>
                            <div style={{ fontSize: 11, color: c.muted }}>
                                {symbol} &nbsp;·&nbsp; {side === 1 ? "BUY" : "SELL"} &nbsp;·&nbsp; {lots} lots &nbsp;·&nbsp; {leverage}x
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            padding: "5px 13px", borderRadius: 7,
                            background: `${outcomeColor}18`, border: `1px solid ${outcomeColor}44`,
                            color: outcomeColor, fontSize: 12, fontWeight: 700,
                        }}>{outcomeLabel}</div>
                        <div style={{
                            padding: "5px 13px", borderRadius: 7,
                            background: isProfit ? "rgba(8,153,129,.12)" : "rgba(242,54,69,.12)",
                            border: `1px solid ${isProfit ? "#08998144" : "#f2364544"}`,
                            color: isProfit ? "#089981" : "#f23645",
                            fontSize: 14, fontWeight: 800,
                            fontFamily: "'SF Mono','Fira Code',monospace",
                        }}>{isProfit ? "▲" : "▼"} {sym}{Math.abs(pnl).toFixed(2)}</div>
                        <button className="pta-close" onClick={onClose} style={{
                            background: "transparent", border: `1px solid ${c.border}`,
                            color: c.muted, borderRadius: 8, width: 34, height: 34,
                            cursor: "pointer", fontSize: 15, transition: ".2s",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>✕</button>
                    </div>
                </div>

                {/* ── Body ── */}
                <div style={{ flex: 1, display: "flex", minHeight: 0 }}>

                    {/* Left: stats */}
                    <div style={{
                        width: 196, flexShrink: 0,
                        borderRight: `1px solid ${c.border}`,
                        padding: "16px 14px", overflowY: "auto", background: c.panel,
                    }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: c.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                            Trade Details
                        </div>
                        <Stat label="Entry" value={entry?.toFixed(2)} dark={dark} />
                        <Stat label="Exit" value={exit?.toFixed(2)} dark={dark} />
                        <Stat label="TP" value={tp?.toFixed(2) ?? "—"} color="#089981" dark={dark} />
                        <Stat label="SL" value={sl?.toFixed(2) ?? "—"} color="#f23645" dark={dark} />
                        <Stat label="R:R" value={`1 : ${rr}`}
                            color={rr === "N/A" ? "#7d8590" : rr >= 2 ? "#089981" : rr >= 1 ? "#ffc107" : "#f23645"}
                            dark={dark} />
                        <Stat label="Lots" value={lots} dark={dark} />
                        <Stat label="Leverage" value={`${leverage}x`} dark={dark} />
                        <Stat label="Margin" value={`${sym}${Number(marginUsed ?? 0).toFixed(2)}`} dark={dark} />
                        <Stat label="PnL" value={`${isProfit ? "+" : ""}${sym}${Math.abs(pnl).toFixed(2)}`}
                            color={isProfit ? "#089981" : "#f23645"} dark={dark} />
                        <Stat label="Symbol" value={symbol} dark={dark} />
                        <Stat label="Direction" value={side === 1 ? "BUY" : "SELL"} dark={dark} />
                        <Stat label="Close Type" value={closeReason} dark={dark} />
                        <Stat label="Entry Value" value={`${sym}${(entry * lots).toFixed(2)}`} dark={dark} />
                        <Stat label="Exit Value" value={`${sym}${(exit * lots).toFixed(2)}`} dark={dark} />
                        <Stat label="Risk Amount" value={sl ? `${sym}${Math.abs((entry - sl) * lots).toFixed(2)}` : "—"} color="#f23645" dark={dark} />
                        <Stat label="Reward Amount" value={tp ? `${sym}${Math.abs((tp - entry) * lots).toFixed(2)}` : "—"} color="#089981" dark={dark} />

                        {/* R:R bar */}
                        {tp && sl && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{ fontSize: 10, color: c.muted, marginBottom: 4 }}>R:R Quality</div>
                                <div style={{ height: 5, borderRadius: 3, background: dark ? "#2a2e39" : "#e0e3eb", overflow: "hidden" }}>
                                    <div style={{
                                        height: "100%",
                                        width: `${Math.min(100, (parseFloat(rr) / 3) * 100)}%`,
                                        background: rr >= 2 ? "#089981" : rr >= 1 ? "#ffc107" : "#f23645",
                                        borderRadius: 3, transition: "width 1s cubic-bezier(.16,1,.3,1)",
                                    }} />
                                </div>
                                <div style={{ fontSize: 10, color: c.muted, marginTop: 3 }}>
                                    {rr >= 2 ? "Excellent" : rr >= 1 ? "Acceptable" : "Poor — below 1:1"}
                                </div>
                            </div>
                        )}

                        {/* Score ring */}
                        {analysis && (
                            <div style={{ marginTop: 22, display: "flex", justifyContent: "center" }}>
                                <ScoreRing score={analysis.score} label={analysis.scoreLabel} color={scoreColor} />
                            </div>
                        )}
                    </div>

                    {/* Right: analysis */}
                    <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                        {analysis && (() => {
                            const bodyText = { fontSize: 12.5, color: dark ? "#c4ccd8" : "#2a2e39", lineHeight: 1.65, margin: 0 };
                            return (
                                <div>
                                    {/* Verdict banner */}
                                    <div className="pta-card" style={{
                                        padding: "14px 18px", borderRadius: 12, marginBottom: 12,
                                        background: isProfit
                                            ? "linear-gradient(135deg,rgba(8,153,129,.18),rgba(8,153,129,.05))"
                                            : "linear-gradient(135deg,rgba(242,54,69,.18),rgba(242,54,69,.05))",
                                        border: `1px solid ${isProfit ? "#08998133" : "#f2364533"}`,
                                        display: "flex", alignItems: "center", gap: 14,
                                        animationDelay: "0s",
                                    }}>
                                        <span style={{ fontSize: 30 }}>{isProfit ? "🏆" : "📉"}</span>
                                        <div>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Verdict</div>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: c.text, lineHeight: 1.4 }}><Md text={analysis.verdict} /></div>
                                        </div>
                                    </div>

                                    {/* What happened */}
                                    <Card icon="📖" title="What Happened" accent="#7c83ff" dark={dark} delay={0.04}>
                                        <p style={bodyText}><Md text={analysis.whatHappened} /></p>
                                    </Card>

                                    {/* Main reason */}
                                    <Card icon={isProfit ? "✅" : "❌"} title={isProfit ? "Primary Success Factor" : "Primary Loss Cause"}
                                        accent={isProfit ? "#089981" : "#f23645"} dark={dark} delay={0.08}>
                                        <p style={bodyText}><Md text={analysis.mainReason} /></p>
                                    </Card>

                                    {/* Entry + SL */}
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                                        <Card icon="🎯" title="Entry Analysis" accent="#2962ff" dark={dark} delay={0.12}>
                                            <p style={{ ...bodyText, fontSize: 12 }}><Md text={analysis.entryAnalysis} /></p>
                                        </Card>
                                        <Card icon="🛡️" title="SL Analysis" accent="#f23645" dark={dark} delay={0.14}>
                                            <p style={{ ...bodyText, fontSize: 12 }}><Md text={analysis.slAnalysis} /></p>
                                        </Card>
                                    </div>

                                    {/* R:R */}
                                    <Card icon="⚖️" title="R:R Analysis" accent="#ffc107" dark={dark} delay={0.18}>
                                        <p style={bodyText}><Md text={analysis.rrAnalysis} /></p>
                                    </Card>

                                    {/* Mistakes + What worked */}
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                                        <Card icon="⚠️" title="Mistakes / Weaknesses" accent="#f23645" dark={dark} delay={0.22}>
                                            <BulletList items={analysis.mistakes} color="#f23645" dark={dark} />
                                        </Card>
                                        <Card icon="💪" title="What Worked" accent="#089981" dark={dark} delay={0.24}>
                                            <BulletList items={analysis.whatWorked} color="#089981" dark={dark} />
                                        </Card>
                                    </div>

                                    {/* Psychology */}
                                    <Card icon="🧠" title="Psychology Note" accent="#a78bfa" dark={dark} delay={0.28}>
                                        <p style={bodyText}><Md text={analysis.psychologyNote} /></p>
                                    </Card>

                                    {/* Actionable tip */}
                                    <div className="pta-card" style={{
                                        padding: "14px 18px", borderRadius: 12, marginBottom: 10,
                                        background: "linear-gradient(135deg,rgba(41,98,255,.14),rgba(41,98,255,.04))",
                                        border: "1px solid rgba(41,98,255,.28)",
                                        display: "flex", gap: 12, alignItems: "flex-start",
                                        animationDelay: "0.32s",
                                    }}>
                                        <span style={{ fontSize: 24, flexShrink: 0 }}>💡</span>
                                        <div>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: "#2962ff", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>
                                                Actionable Tip For Next Trade
                                            </div>
                                            <div style={{ fontSize: 13, color: c.text, lineHeight: 1.6, fontWeight: 600 }}>
                                                <Md text={analysis.actionableTip} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Pre-trade checklist */}
                                    <Card icon="✅" title="Pre-Trade Checklist (Next Time)" accent="#089981" dark={dark} delay={0.36}>
                                        <BulletList items={analysis.nextTradeChecklist} color="#089981" dark={dark} />
                                    </Card>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* ── Footer ── */}
                <div style={{
                    padding: "10px 20px", borderTop: `1px solid ${c.border}`,
                    background: c.panel, display: "flex",
                    justifyContent: "space-between", alignItems: "center", flexShrink: 0,
                }}>
                    <span style={{ fontSize: 11, color: c.muted }}>
                        AI Lubdha · BacktestX
                    </span>
                    <button className="pta-cont" onClick={onClose} style={{
                        padding: "8px 26px", borderRadius: 8, border: "none",
                        background: "#2962ff", color: "white",
                        fontWeight: 700, fontSize: 13, cursor: "pointer", transition: ".2s",
                    }}>
                        Continue Trading
                    </button>
                </div>
            </div>
        </>
    );
};

export default PostTradeAnalysis;